export const SKIP_POLICIES = [
  "blocked",
  "free",
  "approval_required",
] as const;

export type SkipPolicy = (typeof SKIP_POLICIES)[number];

/**
 * Le prove PrimoUp e le configurazioni storiche possono non avere ancora una
 * policy esplicita. In quel caso il flusso esistente resta un salto libero.
 */
export function normalizeSkipPolicy(value: unknown): SkipPolicy {
  return typeof value === "string" &&
    (SKIP_POLICIES as readonly string[]).includes(value)
    ? (value as SkipPolicy)
    : "free";
}

/**
 * Un utente che gestisce soltanto il sottoinsieme delle fasi deve rimandare la
 * policy autorevole senza poterla cambiare tramite una chiamata costruita a
 * mano. Admin e tecnici abilitati possono invece impostarla esplicitamente.
 */
export function enforcePhasePolicyForSave(args: {
  requested: unknown;
  current: unknown;
  canManagePolicies: boolean;
}): SkipPolicy {
  const requested = normalizeSkipPolicy(args.requested);
  const current = normalizeSkipPolicy(args.current);

  if (!args.canManagePolicies && requested !== current) {
    throw new Error(
      "Permesso negato: solo il laboratorio può modificare le regole di salto fase"
    );
  }

  return args.canManagePolicies ? requested : current;
}

export function enforcePhaseSelectionForSave(args: {
  requestedIds: readonly number[];
  currentIds: readonly number[];
  canManagePhases: boolean;
}): void {
  if (args.canManagePhases) return;

  const requested = new Set(args.requestedIds);
  const current = new Set(args.currentIds);
  const isUnchanged =
    requested.size === current.size &&
    Array.from(requested).every((id) => current.has(id));

  if (!isUnchanged) {
    throw new Error(
      "Permesso negato: non puoi modificare le fasi attive della società"
    );
  }
}

export function enforceRemovedPhasePoliciesForSave(args: {
  removed: ReadonlyArray<{
    name: string;
    currentPolicy: unknown;
    catalogPolicy: unknown;
  }>;
  canManagePolicies: boolean;
}): void {
  if (args.canManagePolicies) return;

  const customPhase = args.removed.find(
    (phase) =>
      normalizeSkipPolicy(phase.currentPolicy) !==
      normalizeSkipPolicy(phase.catalogPolicy)
  );
  if (customPhase) {
    throw new Error(
      `Non puoi rimuovere la fase "${customPhase.name}": ha una regola di salto personalizzata. Chiedi al laboratorio di ripristinare prima la regola del catalogo`
    );
  }
}

export function presetStoredPhasePoliciesToFree<
  T extends { skip_policy?: unknown },
>(phases: readonly T[]): {
  phases: Array<Omit<T, "skip_policy"> & { skip_policy: "free" }>;
  policiesUpdated: number;
} {
  let policiesUpdated = 0;
  const normalized = phases.map((phase) => {
    if (phase.skip_policy !== "free") policiesUpdated += 1;
    return { ...phase, skip_policy: "free" as const };
  });

  return { phases: normalized, policiesUpdated };
}

/**
 * Aggiorna soltanto policy già presenti nel JSON/array del catalogo. I test
 * senza campo continuano a usare il fallback `free`, senza riscrivere dati o
 * perdere slug e metadati PrimoUp.
 */
export function presetExplicitCatalogPoliciesToFree(raw: unknown): {
  value: unknown;
  policiesUpdated: number;
} {
  const wasSerialized = typeof raw === "string";
  let parsed: unknown = raw;

  if (wasSerialized) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { value: raw, policiesUpdated: 0 };
    }
  }
  if (!Array.isArray(parsed)) {
    return { value: raw, policiesUpdated: 0 };
  }

  let policiesUpdated = 0;
  const tests = parsed.map((test) => {
    if (
      !test ||
      typeof test !== "object" ||
      !Object.prototype.hasOwnProperty.call(test, "skip_policy") ||
      (test as Record<string, unknown>).skip_policy === "free"
    ) {
      return test;
    }

    policiesUpdated += 1;
    return { ...(test as Record<string, unknown>), skip_policy: "free" };
  });

  if (policiesUpdated === 0) {
    return { value: raw, policiesUpdated: 0 };
  }
  return {
    value: wasSerialized ? JSON.stringify(tests) : tests,
    policiesUpdated,
  };
}
